import {
  getRendererHostAdapter,
  type BustlyWorkspaceIntegrationsOptions,
} from "../platform/host";
import { createGatewayInstanceId } from "./gateway-instance-id";
import { getBrowserInitialLocale } from "../i18n/locale";
import { readReusableSkillSuggestionsPreference } from "./reusable-skill-suggestions-preference";
import {
  RuntimeRequestError,
  RuntimeTransportStoppedError,
  type RuntimeEventFrame,
  type RuntimeTransport,
} from "./runtime-transport";
import {
  createRuntimeTransport,
  shouldUseElectronRuntimeTransport,
} from "./runtime-transport-resolver";

type RuntimeMethodRequest = {
  method: string;
  payload?: unknown;
  scope?: string;
};

export class SuppressedBustlyRuntimeApiError extends Error {
  readonly originalError: unknown;

  constructor(message: string, originalError: unknown) {
    super(message);
    this.name = "SuppressedBustlyRuntimeApiError";
    this.originalError = originalError;
  }
}

export function isSuppressedBustlyRuntimeApiError(
  error: unknown,
): error is SuppressedBustlyRuntimeApiError {
  return error instanceof SuppressedBustlyRuntimeApiError;
}

type GatewayEventListener = (event: RuntimeEventFrame) => void;

export type BustlyResolvedPathAccess = {
  kind: "file" | "directory" | "missing" | "forbidden";
  name: string;
  mimeType: string | null;
  mediaKind: "image" | "video" | "audio" | "document" | null;
  previewPath: string | null;
  downloadPath: string | null;
  openPath: string | null;
  archivePath: string | null;
};

export type BustlyImportedPathResult = {
  path: string;
  name: string;
  kind: "file";
};

export type BustlySessionLabelUpdatedPayload = {
  agentId: string;
  sessionKey: string;
  label: string;
  updatedAt: number | null;
};

export type BustlyAgentsUpdatedPayload = {
  workspaceId: string;
  updated: number;
};

export type SelfEvolutionSuggestionStatus =
  | "proposed"
  | "approved"
  | "applying"
  | "drafted"
  | "applied"
  | "dismissed"
  | "failed"
  | "retrying";

export type SelfEvolutionSuggestion = {
  version: 1;
  id: string;
  status: SelfEvolutionSuggestionStatus;
  layer: "memory" | "workflow_skill";
  memoryType?: "short_term" | "long_term";
  workflowType?: "task_workflow" | "integration_write";
  title: string;
  preview: string;
  reason: string;
  confidence: number;
  language: string;
  source: {
    runId?: string;
    sessionId: string;
    sessionKey?: string;
    agentId: string;
    agentDir: string;
    sessionFile: string;
    workspaceDir: string;
    workspaceId?: string;
  };
  sourceModel: {
    provider: string;
    model: string;
    selectionSource:
      | "source_run_selected"
      | "session_current"
      | "default_fallback";
  };
  evidence?: {
    taskSignals: string[];
    executionSteps: string[];
    deliverables: string[];
    completionSignals: string[];
    integrationKeys?: string[];
    writeOperations?: string[];
  };
  payload: Record<string, string>;
  result?: {
    status: "written" | "uploaded" | "no_write" | "failed";
    summary: string;
    artifacts?: Record<string, string | null | undefined>;
    reason?: string;
  };
  memoryDraft?: {
    memoryType: "short_term" | "long_term";
    heading: string;
    body: string;
    summary?: string;
    generatedAt: string;
  };
  draft?: {
    skillKey: string;
    displayName: string;
    description: string;
    body: string;
    summary?: string;
    generatedAt: string;
  };
  error?: { code: string; message: string };
  createdAt: string;
  updatedAt: string;
  approvedAt?: string;
  dismissedAt?: string;
  appliedAt?: string;
};

export type SelfEvolutionSuggestionUpdatedPayload = {
  suggestion: SelfEvolutionSuggestion;
};

export type SelfEvolutionReviewStatus = "evaluating" | "completed" | "failed";

export type SelfEvolutionReviewStatusPayload = {
  sessionKey: string;
  runId: string;
  reviewRunId: string;
  status: SelfEvolutionReviewStatus;
  suggestionCount?: number;
  suggestions?: SelfEvolutionSuggestion[];
  errorMessage?: string;
};

export type BustlyAgentUseCase = {
  label: string;
  prompt: string;
};

export type BustlyAgentConfig = {
  slug: string;
  label: string;
  icon: string;
  skills: string[];
  useCases: BustlyAgentUseCase[];
};

export type BustlyOnboardingBootstrapPromptVersions = {
  agents?: number;
  heartbeat?: number;
  metadata?: number;
};

export type BustlyOnboardingBootstrapPreview = {
  requestedRoleKey: string;
  roleKey: string;
  locale: "en" | "zh-CN";
  label: string;
  agentName: string;
  agentsMarkdown: string;
  heartbeatMarkdown: string;
  metadata: {
    label?: string;
    icon?: string;
    skills?: string[];
  };
  promptNames: {
    agents: string;
    heartbeat: string;
    metadata: string;
  };
  promptVersions: Required<BustlyOnboardingBootstrapPromptVersions>;
};

export type AgentRunLedgerCloudConnectionSummary = {
  resolved?: boolean;
  displayName?: string;
};

export type AgentRunLedgerCloudEventSummary = {
  eventId: string;
  summaryText?: string;
  platformLabel?: string;
  operationLabel?: string;
  resourceLabel?: string;
  statusLabel?: string;
  changedFields?: string[];
  connection?: AgentRunLedgerCloudConnectionSummary;
  hasContent?: boolean;
  canViewContent?: boolean;
};

export type AgentRunLedgerCloudEventsListResult = {
  events: AgentRunLedgerCloudEventSummary[];
};

export type AgentRunLedgerCloudContentResult =
  | { ok: true; content: unknown; status: number }
  | { ok: false; forbidden?: boolean; status: number; error?: string };

export type AgentRunLedgerCloudUploadResult = {
  attempted: number;
  accepted: number;
  duplicate: number;
  rejected: number;
  failed: number;
  retryable: boolean;
  status?: number;
};

export type AgentRunLedgerEvent = {
  runId: string;
  seq: number;
  ts: string;
  phase: string;
  status: string;
  source: string;
  pid: number;
  schemaVersion: number;
  data?: Record<string, unknown>;
  error?: Record<string, unknown>;
  content?: unknown;
};

export type AgentRunTimelineResult = {
  runId: string;
  resolvedFrom?: string;
  projection?: Record<string, unknown>;
  events: AgentRunLedgerEvent[];
  nextSeq?: number;
  degraded: boolean;
  health: Record<string, unknown>;
};

export type AgentRunTimelineParams = {
  runId: string;
  sinceSeq?: number;
  limit?: number;
};

export type BustlyCronSchedule =
  | { kind: "at"; at: string }
  | { kind: "every"; everyMs: number; anchorMs?: number }
  | { kind: "cron"; expr: string; tz?: string; staggerMs?: number };

export type BustlyCronSessionTarget = "main" | "isolated";
export type BustlyCronWakeMode = "next-heartbeat" | "now";
export type BustlyCronDeliveryMode = "none" | "announce" | "webhook";
export type BustlyCronRunStatus = "ok" | "error" | "skipped";
export type BustlyCronDeliveryStatus =
  | "delivered"
  | "not-delivered"
  | "unknown"
  | "not-requested";

export type BustlyCronDelivery = {
  mode: BustlyCronDeliveryMode;
  channel?: string;
  to?: string;
  bestEffort?: boolean;
};

export type BustlyCronDeliveryPatch = Partial<BustlyCronDelivery>;

export type BustlyCronPayload =
  | { kind: "systemEvent"; text: string }
  | {
      kind: "agentTurn";
      message: string;
      model?: string;
      thinking?: string;
      timeoutSeconds?: number;
      allowUnsafeExternalContent?: boolean;
      deliver?: boolean;
      channel?: string;
      to?: string;
      bestEffortDeliver?: boolean;
    };

export type BustlyCronPayloadPatch =
  | { kind: "systemEvent"; text?: string }
  | {
      kind: "agentTurn";
      message?: string;
      model?: string;
      thinking?: string;
      timeoutSeconds?: number;
      allowUnsafeExternalContent?: boolean;
      deliver?: boolean;
      channel?: string;
      to?: string;
      bestEffortDeliver?: boolean;
    };

export type BustlyCronJobState = {
  nextRunAtMs?: number;
  runningAtMs?: number;
  lastRunAtMs?: number;
  lastRunStatus?: BustlyCronRunStatus;
  lastStatus?: BustlyCronRunStatus;
  lastError?: string;
  lastDurationMs?: number;
  consecutiveErrors?: number;
  scheduleErrorCount?: number;
  lastDeliveryStatus?: BustlyCronDeliveryStatus;
  lastDeliveryError?: string;
  lastDelivered?: boolean;
};

export type BustlyCronJob = {
  id: string;
  agentId?: string;
  sessionKey?: string;
  reuseSession?: boolean;
  name: string;
  description?: string;
  enabled: boolean;
  deleteAfterRun?: boolean;
  createdAtMs: number;
  updatedAtMs: number;
  schedule: BustlyCronSchedule;
  sessionTarget: BustlyCronSessionTarget;
  wakeMode: BustlyCronWakeMode;
  payload: BustlyCronPayload;
  delivery?: BustlyCronDelivery;
  state: BustlyCronJobState;
};

export type BustlyCronJobCreate = {
  name: string;
  description?: string;
  agentId?: string | null;
  sessionKey?: string | null;
  reuseSession?: boolean;
  enabled?: boolean;
  deleteAfterRun?: boolean;
  schedule: BustlyCronSchedule;
  sessionTarget: BustlyCronSessionTarget;
  wakeMode: BustlyCronWakeMode;
  payload: BustlyCronPayload;
  delivery?: BustlyCronDelivery;
  state?: Partial<BustlyCronJobState>;
};

export type BustlyCronJobPatch = Partial<
  Omit<
    BustlyCronJob,
    "id" | "createdAtMs" | "updatedAtMs" | "state" | "payload" | "delivery"
  >
> & {
  agentId?: string | null;
  sessionKey?: string | null;
  payload?: BustlyCronPayloadPatch;
  delivery?: BustlyCronDeliveryPatch;
  state?: Partial<BustlyCronJobState>;
};

export type BustlyCronStatusSummary = {
  enabled: boolean;
  storePath: string;
  jobs: number;
  nextWakeAtMs: number | null;
};

export type BustlyCronListPageResult = {
  jobs: BustlyCronJob[];
  total: number;
  offset: number;
  limit: number;
  hasMore: boolean;
  nextOffset: number | null;
};

export type BustlyCronEvent = {
  jobId: string;
  action: "added" | "updated" | "removed" | "started" | "finished";
  runAtMs?: number;
  durationMs?: number;
  status?: BustlyCronRunStatus;
  error?: string;
  summary?: string;
  delivered?: boolean;
  deliveryStatus?: BustlyCronDeliveryStatus;
  deliveryError?: string;
  sessionId?: string;
  sessionKey?: string;
  nextRunAtMs?: number;
  model?: string;
  provider?: string;
  usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    cache_read_tokens?: number;
    cache_write_tokens?: number;
  };
};

export type BustlyHeartbeatEvent = {
  id: string;
  agentId: string;
  agentName: string;
  severity: "critical" | "warning" | "suggestion";
  title: string;
  message: string;
  actionPrompt: string;
  status: "open" | "seen" | "actioned";
  createdAt: number;
  updatedAt: number;
};

export type BustlyHeartbeatCard = {
  agentId: string;
  agentName: string;
  title: string;
  content: string;
  enabled: boolean;
  createdAt: number | null;
  updatedAt: number | null;
  lastScanAt: number | null;
  every: string | null;
  openEvents: BustlyHeartbeatEvent[];
};

export type BustlyHeartbeatHealth = {
  score: number;
  status: "Healthy" | "Warning" | "Critical";
  counts: {
    critical: number;
    warning: number;
    suggestion: number;
  };
  lastScanAt: number | null;
};

export type BustlyHeartbeatListResult = {
  workspaceId: string;
  initialized: boolean;
  hasEvents: boolean;
  heartbeats: BustlyHeartbeatCard[];
  priorityEvents: BustlyHeartbeatEvent[];
  health: BustlyHeartbeatHealth | null;
};

export type BustlyHeartbeatEventsListResult = {
  workspaceId: string;
  events: BustlyHeartbeatEvent[];
};

export type BustlyHeartbeatRunningResult = {
  workspaceId: string;
  hasRunningHeartbeats: boolean;
  runs: Array<{
    agentId: string;
    sessionKey: string;
    startedAt: number;
    reason: string | null;
  }>;
};

export type BustlyHeartbeatEventStatusUpdateResult = {
  workspaceId: string;
  event: BustlyHeartbeatEvent;
};

export type BustlyHeartbeatGatewayEventRecord = Omit<
  BustlyHeartbeatEvent,
  "agentName"
>;

export type BustlyHeartbeatGatewayEvent = {
  kind: "heartbeat";
  ts: number;
  workspaceId: string;
  agentId: string;
  action: "opened" | "reopened" | "status-updated";
  event: BustlyHeartbeatGatewayEventRecord;
};

export type BustlyLegacyHeartbeatGatewayEvent = {
  ts?: number;
  status?: string;
  to?: string;
  accountId?: string;
  preview?: string;
  durationMs?: number;
  hasMedia?: boolean;
  reason?: string;
  channel?: string;
  silent?: boolean;
  indicatorType?: "ok" | "alert" | "error";
};

export type BustlyHeartbeatGatewayPayload =
  | BustlyHeartbeatGatewayEvent
  | BustlyLegacyHeartbeatGatewayEvent
  | null;

export type BustlyChatEvent = {
  runId?: string;
  sessionKey?: string;
  state?: "final" | "error" | "aborted" | string;
  message?: unknown;
  errorMessage?: string;
};

type BustlyLoginStartResult = {
  loginUrl: string;
  loginTraceId: string;
};

let sharedEventTransport: RuntimeTransport | null = null;
let sharedEventTransportStarting = false;
const sharedEventListeners = new Set<GatewayEventListener>();
let sharedRequestTransport: RuntimeTransport | null = null;
let sharedRequestTransportReadyPromise: Promise<RuntimeTransport> | null = null;

async function ensureGatewayConnectConfig(): Promise<GatewayConnectConfig> {
  const host = getRendererHostAdapter();
  const gatewayStatus = await host.gatewayStatus();
  if (!gatewayStatus.running) {
    const startResult = await host.gatewayStart();
    if (!startResult.success) {
      throw new Error(startResult.error ?? "Gateway is not running.");
    }
  }

  const connection = await host.gatewayConnectConfig();
  if (!connection.token || !connection.wsUrl) {
    throw new Error("Gateway token missing in config; cannot connect.");
  }
  return connection;
}

function resolveGatewayHttpOrigin(wsUrl: string): string {
  const parsed = new URL(wsUrl);
  parsed.protocol = parsed.protocol === "wss:" ? "https:" : "http:";
  parsed.pathname = "/";
  parsed.search = "";
  parsed.hash = "";
  return parsed.toString();
}

function resolveSystemLanguage(): string {
  if (typeof navigator === "undefined") {
    return "";
  }
  const preferred =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages.find(
          (value) => typeof value === "string" && value.trim().length > 0,
        )
      : undefined;
  const fallback =
    typeof navigator.language === "string" ? navigator.language : "";
  return (preferred ?? fallback).trim();
}

export async function buildBustlyGatewayHttpUrl(
  routePath: string,
): Promise<string> {
  const connection = await ensureGatewayConnectConfig();
  return new URL(
    routePath,
    resolveGatewayHttpOrigin(connection.wsUrl),
  ).toString();
}

export async function initializeBustlyGatewayIfNeeded(): Promise<void> {
  const host = getRendererHostAdapter();
  const gatewayStatus = await host.gatewayStatus();
  if (gatewayStatus.initialized) {
    return;
  }
  const result = await host.openclawInit();
  if (!result.success) {
    throw new Error(result.error ?? "Failed to initialize OpenClaw.");
  }
}

function disposeSharedRequestTransport(): void {
  sharedRequestTransport?.stop();
  sharedRequestTransport = null;
  sharedRequestTransportReadyPromise = null;
}

function shouldRetryRuntimeMethod(error: unknown): boolean {
  if (error instanceof RuntimeTransportStoppedError) {
    return true;
  }
  if (error instanceof RuntimeRequestError) {
    const code = error.gatewayCode ?? error.runtimeCode;
    return code === "transport_closed" || code === "gateway_error";
  }
  if (error instanceof Error) {
    return (
      error.message.includes("gateway not connected") ||
      error.message.includes("gateway closed") ||
      error.message.includes("Gateway disconnected")
    );
  }
  return false;
}

async function ensureSharedRequestTransport(): Promise<RuntimeTransport> {
  if (sharedRequestTransport && sharedRequestTransportReadyPromise) {
    return await sharedRequestTransportReadyPromise;
  }
  if (sharedRequestTransportReadyPromise) {
    return await sharedRequestTransportReadyPromise;
  }

  sharedRequestTransportReadyPromise = (async () => {
    const connection = shouldUseElectronRuntimeTransport()
      ? null
      : await ensureGatewayConnectConfig();
    return await new Promise<RuntimeTransport>((resolve, reject) => {
      let ready = false;
      const transport = createRuntimeTransport({
        gateway: connection
          ? {
              url: connection.wsUrl,
              token: connection.token ?? undefined,
              resolveConnection: async () => {
                const next = await ensureGatewayConnectConfig();
                return {
                  url: next.wsUrl,
                  token: next.token ?? undefined,
                };
              },
            }
          : undefined,
        clientName: "openclaw-control-ui",
        mode: "ui",
        instanceId: createGatewayInstanceId("bustly-rpc"),
        onHello: () => {
          ready = true;
          sharedRequestTransport = transport;
          resolve(transport);
        },
        onClose: ({ error, reason }) => {
          const closeError = new Error(
            error?.message || reason || "Runtime disconnected.",
          );
          if (!ready) {
            disposeSharedRequestTransport();
            reject(closeError);
            return;
          }
          if (sharedRequestTransport === transport) {
            disposeSharedRequestTransport();
          }
        },
      });
      transport.start();
    });
  })();

  try {
    return await sharedRequestTransportReadyPromise;
  } catch (error) {
    disposeSharedRequestTransport();
    throw error;
  }
}

export async function requestBustlyRuntimeMethod<T>(
  params: RuntimeMethodRequest,
): Promise<T> {
  const transport = await ensureSharedRequestTransport();
  try {
    return await transport.request<T>(params.method, params.payload);
  } catch (error) {
    if (!shouldRetryRuntimeMethod(error)) {
      throw error;
    }
    disposeSharedRequestTransport();
    const retryTransport = await ensureSharedRequestTransport();
    return await retryTransport.request<T>(params.method, params.payload);
  }
}

export const requestBustlyGatewayMethod = requestBustlyRuntimeMethod;

async function requestSuppressedBustlyRuntimeMethod<T>(
  params: RuntimeMethodRequest,
  message: string,
): Promise<T> {
  try {
    return await requestBustlyRuntimeMethod<T>(params);
  } catch (error) {
    throw new SuppressedBustlyRuntimeApiError(message, error);
  }
}

async function startSharedEventTransport(): Promise<void> {
  if (
    sharedEventTransport ||
    sharedEventTransportStarting ||
    sharedEventListeners.size === 0
  ) {
    return;
  }
  sharedEventTransportStarting = true;
  try {
    const connection = shouldUseElectronRuntimeTransport()
      ? null
      : await ensureGatewayConnectConfig();
    if (sharedEventListeners.size === 0) {
      return;
    }
    const transport = createRuntimeTransport({
      gateway: connection
        ? {
            url: connection.wsUrl,
            token: connection.token ?? undefined,
            resolveConnection: async () => {
              const next = await ensureGatewayConnectConfig();
              return {
                url: next.wsUrl,
                token: next.token ?? undefined,
              };
            },
          }
        : undefined,
      clientName: "openclaw-control-ui",
      mode: "ui",
      instanceId: createGatewayInstanceId("bustly-events"),
      onEvent: (event) => {
        for (const listener of [...sharedEventListeners]) {
          try {
            listener(event);
          } catch (error) {
            console.warn("[bustly-gateway] event listener failed", error);
          }
        }
      },
      onClose: () => {
        if (sharedEventListeners.size === 0) {
          sharedEventTransport = null;
        }
      },
    });
    sharedEventTransport = transport;
    transport.start();
  } finally {
    sharedEventTransportStarting = false;
  }
}

function stopSharedEventTransportIfIdle(): void {
  if (sharedEventListeners.size > 0) {
    return;
  }
  sharedEventTransport?.stop();
  sharedEventTransport = null;
}

function subscribeBustlyGatewayEvent<T>(
  eventName: string,
  listener: (payload: T) => void,
): () => void {
  const wrappedListener: GatewayEventListener = (event) => {
    if (event.event !== eventName) {
      return;
    }
    listener((event.payload ?? null) as T);
  };
  sharedEventListeners.add(wrappedListener);
  void startSharedEventTransport().catch((error) => {
    console.warn(
      "[bustly-gateway] failed to start shared event transport",
      error,
    );
  });
  return () => {
    sharedEventListeners.delete(wrappedListener);
    stopSharedEventTransportIfIdle();
  };
}

export function subscribeBustlySessionLabelUpdated(
  listener: (payload: BustlySessionLabelUpdatedPayload) => void,
): () => void {
  return subscribeBustlyGatewayEvent<BustlySessionLabelUpdatedPayload>(
    "bustly.session.label.updated",
    listener,
  );
}

export function subscribeBustlyAgentsUpdated(
  listener: (payload: BustlyAgentsUpdatedPayload) => void,
): () => void {
  return subscribeBustlyGatewayEvent<BustlyAgentsUpdatedPayload>(
    "bustly.agents.updated",
    listener,
  );
}

export async function getBustlySupabaseConfig(): Promise<BustlySupabaseConfig | null> {
  return await requestBustlyRuntimeMethod<BustlySupabaseConfig | null>({
    method: "bustly.supabase.get-config",
    scope: "bustly-supabase",
  });
}

export async function isBustlyLoggedIn(): Promise<boolean> {
  const result = await getRendererHostAdapter().bustlyIsLoggedIn();
  if (!result.success) {
    throw new Error(result.error || "Failed to check Bustly login status.");
  }
  return result.loggedIn === true;
}

export async function startBustlyLogin(): Promise<BustlyLoginStartResult> {
  const result = await getRendererHostAdapter().bustlyLogin();
  if (!result.success || !result.loginTraceId) {
    throw new Error(result.error || "Failed to start Bustly login.");
  }
  return {
    loginTraceId: result.loginTraceId,
    loginUrl: "",
  };
}

export async function pollBustlyLogin(
  loginTraceId: string,
): Promise<{ pending: boolean }> {
  const result = await getRendererHostAdapter().bustlyPollLogin(loginTraceId);
  if (!result.success) {
    throw new Error(result.error || "Failed to poll Bustly login.");
  }
  return { pending: result.pending };
}

export async function cancelBustlyLogin(loginTraceId?: string): Promise<void> {
  const result = await getRendererHostAdapter().bustlyCancelLogin(loginTraceId);
  if (!result.success) {
    throw new Error(result.error || "Failed to cancel Bustly login.");
  }
}

export async function getBustlyUserInfo(): Promise<BustlyUserInfo | null> {
  const result = await getRendererHostAdapter().bustlyGetUserInfo();
  if (!result.success) {
    throw new Error(result.error || "Failed to load Bustly user info.");
  }
  return result.user ?? null;
}

export async function getActiveBustlyWorkspace(): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.workspace.get-active",
    scope: "bustly-workspace",
  });
}

export async function bootstrapBustlyRuntime(params?: {
  workspaceId?: string;
  workspaceName?: string;
  agentName?: string;
  selectedModel?: string;
  skipAgentBootstrap?: boolean;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
}> {
  return await requestSuppressedBustlyRuntimeMethod(
    {
      method: "bustly.runtime.bootstrap",
      payload: params ?? {},
      scope: "bustly-runtime",
    },
    "Bustly workspace runtime bootstrap failed.",
  );
}

export async function prepareBustlyRuntime(params?: {
  workspaceId?: string;
  workspaceName?: string;
  agentName?: string;
  selectedModel?: string;
  skipAgentBootstrap?: boolean;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
}> {
  return await requestSuppressedBustlyRuntimeMethod(
    {
      method: "bustly.runtime.prepare",
      payload: params ?? {},
      scope: "bustly-runtime",
    },
    "Bustly workspace runtime prepare failed.",
  );
}

export async function warmupBustlyRuntime(params?: {
  workspaceId?: string;
  workspaceName?: string;
  agentName?: string;
  selectedModel?: string;
  skipAgentBootstrap?: boolean;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
}> {
  return await requestSuppressedBustlyRuntimeMethod(
    {
      method: "bustly.runtime.warmup",
      payload: params ?? {},
      scope: "bustly-runtime",
    },
    "Bustly workspace runtime warmup failed.",
  );
}

export async function reportBustlyUserLanguage(params?: {
  language?: string;
  source?: string;
}): Promise<void> {
  const language = (params?.language ?? resolveSystemLanguage()).trim();
  if (!language) {
    return;
  }
  await requestBustlyRuntimeMethod({
    method: "bustly.runtime.user-language.set",
    payload: {
      language,
      source: params?.source ?? "client-app",
    },
    scope: "bustly-runtime-user-language",
  });
}

export async function setActiveBustlyWorkspace(params: {
  workspaceId: string;
  workspaceName?: string;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
}> {
  return await requestSuppressedBustlyRuntimeMethod(
    {
      method: "bustly.workspace.set-active",
      payload: params,
      scope: "bustly-workspace",
    },
    "Bustly workspace switch failed.",
  );
}

export async function listBustlyAgents(
  workspaceId?: string,
  options?: {
    includeArchived?: boolean;
    archivedOnly?: boolean;
    forceExpertSync?: boolean;
    agentId?: string;
    locale?: "en" | "zh-CN";
  },
): Promise<BustlyWorkspaceAgent[]> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.agents.list",
    payload: {
      ...(workspaceId ? { workspaceId } : {}),
      ...(options?.includeArchived ? { includeArchived: true } : {}),
      ...(options?.archivedOnly ? { archivedOnly: true } : {}),
      ...(options?.forceExpertSync ? { forceExpertSync: true } : {}),
      ...(options?.forceExpertSync && options.agentId ? { agentId: options.agentId } : {}),
      locale: options?.locale ?? getBrowserInitialLocale(),
    },
    scope: "bustly-agents",
  });
}

export async function createBustlyAgent(params: {
  workspaceId: string;
  /** Stable internal key for predefined experts; `name` remains user-facing. */
  agentName?: string;
  name?: string;
  description?: string;
  icon?: string;
  workspaceName?: string;
  skills?: string[] | null;
  bootstrapRoleKey?: string;
  bootstrapLocale?: "en" | "zh-CN";
  bootstrapPromptVersions?: BustlyOnboardingBootstrapPromptVersions;
  bootstrapAgentsMarkdown?: string;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
  onboardingBootstrap?: {
    roleKey: string;
    locale: "en" | "zh-CN";
    promptVersions: Required<BustlyOnboardingBootstrapPromptVersions>;
  };
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.agents.create",
    payload: params,
    scope: "bustly-agents",
  });
}

export type BustlyExpertAgentProfile = {
  slug: string;
  label: string;
  clientVisible?: boolean;
  description?: string;
  identityMarkdown: string;
  icon?: string;
  roleType: "core" | "specialist";
  category: string;
  responsibilityTags?: string[];
  useCases?: BustlyAgentUseCase[];
  coreSkillRefs?: string[];
  onDemandSkillRefs?: string[];
  privateSkillRefs?: string[];
  integrationRefs?: string[];
  integrationGroups?: Array<{ refs: string[]; purpose: string }>;
  legacyAliases?: string[];
};

export type BustlyExpertCatalogProfile = Omit<
  BustlyExpertAgentProfile,
  "identityMarkdown"
> & {
  /** Returned only for an explicit single-profile detail request. */
  identityMarkdown?: string;
  source:
    | { kind: "desktop-catalog" }
    | { kind: "BUSTLY_RUNTIME_CATALOG"; locale: "en" | "zh-CN" }
    | {
        kind: "runtime-assets";
        locale: "en" | "zh-CN";
        agents: {
          key: string;
          version: number;
          sourceDigest: `sha256:${string}`;
        };
        metadata: {
          key: string;
          version: number;
          sourceDigest: `sha256:${string}`;
        };
      };
  freshness: "fresh" | "stale" | "fallback";
};

export type BustlyExpertCatalogProfilesResult = {
  workspaceId: string;
  locale: "en" | "zh-CN";
  profiles: BustlyExpertCatalogProfile[];
  degraded: boolean;
};

export async function resolveBustlyExpertCatalogProfiles(params: {
  workspaceId: string;
  profiles: BustlyExpertAgentProfile[];
  locale?: string;
  forceRefresh?: boolean;
  /** Avoids loading every large Expert prompt on the catalog screen. */
  includeIdentity?: boolean;
}): Promise<BustlyExpertCatalogProfilesResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.experts.resolve-profiles",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function ensureBustlyExpertAgent(params: {
  workspaceId: string;
  profile: BustlyExpertAgentProfile;
  locale?: string;
}): Promise<{
  workspaceId: string;
  agentId: string;
  workspaceDir: string;
  created: boolean;
  skillPreparation?: {
    installed: string[];
    pendingRestart: string[];
    failures: Array<{ resource: string; error: string }>;
  };
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.experts.ensure-agent",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function getBustlyOnboardingBootstrapPreview(params: {
  workspaceId: string;
  workspaceName?: string;
  roleKey?: string | null;
  locale?: "en" | "zh-CN";
}): Promise<BustlyOnboardingBootstrapPreview> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.onboarding.bootstrap-preview",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function updateBustlyAgent(params: {
  workspaceId: string;
  agentId: string;
  name?: string;
  identityMarkdown?: string;
  icon?: string;
  skills?: string[] | null;
  assignedSkills?: string[] | null;
}): Promise<{ ok: true; workspaceId: string; agentId: string }> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.agents.update",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function archiveBustlyAgent(params: {
  workspaceId: string;
  agentId: string;
  archived?: boolean;
}): Promise<{
  ok: true;
  workspaceId: string;
  agentId: string;
  archived: boolean;
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.agents.archive",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function deleteBustlyAgent(params: {
  workspaceId: string;
  agentId: string;
}): Promise<{
  ok: true;
  workspaceId: string;
  agentId: string;
  archived?: boolean;
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.agents.delete",
    payload: params,
    scope: "bustly-agents",
  });
}

export async function listBustlyAgentSessions(params: {
  workspaceId: string;
  agentId: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
}): Promise<BustlyWorkspaceAgentSession[]> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.sessions.list",
    payload: params,
    scope: "bustly-sessions",
  });
}

export async function createBustlyAgentSession(params: {
  workspaceId: string;
  agentId: string;
  label?: string;
  promptExcerpt?: string;
  sampleRouteKey?: string;
}): Promise<{
  workspaceId: string;
  agentId: string;
  sessionKey: string;
  sessionId: string;
  name: string;
  updatedAt: number | null;
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.sessions.create",
    payload: params,
    scope: "bustly-sessions",
  });
}

export async function patchBustlySessionModel(params: {
  sessionKey: string;
  model: string;
}): Promise<string> {
  const result = await requestBustlyRuntimeMethod<{
    ok: boolean;
    resolved?: { model?: string };
  }>({
    method: "sessions.patch",
    payload: {
      key: params.sessionKey,
      model: params.model,
    },
    scope: "bustly-sessions",
  });
  return result.resolved?.model?.trim() || params.model.trim();
}

export async function patchBustlySessionLabel(params: {
  sessionKey: string;
  label: string;
}): Promise<string> {
  const label = params.label.trim();
  const result = await requestBustlyRuntimeMethod<{
    ok: boolean;
    entry?: { label?: string };
  }>({
    method: "sessions.patch",
    payload: {
      key: params.sessionKey,
      label,
    },
    scope: "bustly-sessions",
  });
  return result.entry?.label?.trim() || label;
}

export async function archiveBustlySession(params: {
  sessionKey: string;
  archived?: boolean;
}): Promise<boolean> {
  const result = await requestBustlyRuntimeMethod<{
    ok: boolean;
    entry?: { archived?: boolean };
  }>({
    method: "sessions.patch",
    payload: {
      key: params.sessionKey,
      archived: params.archived !== false,
    },
    scope: "bustly-sessions",
  });
  return result.entry?.archived === true;
}

export async function sendBustlySessionMessage(params: {
  sessionKey: string;
  message: string;
  idempotencyKey: string;
  postRunSelfEvolutionEnabled?: boolean;
  attachments?: Array<{
    type: "image";
    mimeType: string;
    content: string;
  }>;
}): Promise<{
  runId?: string;
  status?: string;
}> {
  return await requestBustlyRuntimeMethod({
    method: "chat.send",
    payload: {
      sessionKey: params.sessionKey,
      message: params.message,
      deliver: false,
      idempotencyKey: params.idempotencyKey,
      postRunSelfEvolutionEnabled:
        params.postRunSelfEvolutionEnabled ??
        readReusableSkillSuggestionsPreference(),
      attachments:
        params.attachments && params.attachments.length > 0
          ? params.attachments
          : undefined,
    },
    scope: `bustly-chat-send-${params.sessionKey}`,
  });
}

export async function listGlobalSkillCatalog(): Promise<
  BustlyGlobalSkillCatalogItem[]
> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.list",
    scope: "skills-catalog",
  });
}

export type SkillCatalogMutationResult = {
  ok: boolean;
  skillKey: string;
  status?: string;
  restartRequired?: boolean;
};

export type EditableCustomSkill = {
  skillKey: string;
  displayName: string;
  description: string;
  body: string;
};

export type EditableCustomSkillUpdateResult = EditableCustomSkill & {
  cloudSync?:
    | {
        status: "synced" | "already_synced";
        skillKey: string;
        skillId: string;
        versionId: string;
        contentSha256: string;
      }
    | {
        status: "skipped";
        reason: "empty" | "unstable" | "unauthenticated";
      }
    | { status: "failed"; error: string };
};

export async function getEditableCustomSkill(
  skillKey: string,
): Promise<EditableCustomSkill> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.custom.get",
    payload: { skillKey },
    scope: `skills-catalog-custom-get-${skillKey}`,
  });
}

export async function updateEditableCustomSkill(
  params: EditableCustomSkill,
): Promise<EditableCustomSkillUpdateResult> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.custom.update",
    payload: params,
    scope: `skills-catalog-custom-update-${params.skillKey}`,
  });
}

export async function installGlobalSkillCatalogItem(
  skillKey: string,
): Promise<SkillCatalogMutationResult> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.install",
    payload: { skillKey },
    scope: `skills-catalog-install-${skillKey}`,
  });
}

export async function installWorkspaceGlobalSkillCatalogItem(
  skillId: string,
): Promise<void> {
  await requestBustlyRuntimeMethod({
    method: "skills.catalog.install-workspace",
    payload: { skillId },
    scope: `skills-catalog-install-workspace-${skillId}`,
  });
}

export async function installUploadedGlobalSkillCatalogItem(
  path: string,
): Promise<{
  ok: boolean;
  path: string;
  skillKey: string;
  name?: string;
  displayName?: string;
  skillName?: string;
  installDir: string;
  sourceKind: "file" | "directory";
}> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.upload",
    payload: { path },
    scope: "skills-catalog-upload",
  });
}

export async function updateGlobalSkillCatalogItem(
  skillKey: string,
): Promise<SkillCatalogMutationResult> {
  return await requestBustlyRuntimeMethod({
    method: "skills.catalog.update",
    payload: { skillKey },
    scope: `skills-catalog-update-${skillKey}`,
  });
}

export async function uninstallGlobalSkillCatalogItem(
  skillKey: string,
): Promise<void> {
  await requestBustlyRuntimeMethod({
    method: "skills.catalog.uninstall",
    payload: { skillKey },
    scope: `skills-catalog-uninstall-${skillKey}`,
  });
}

export async function logoutBustly(): Promise<void> {
  const result = await getRendererHostAdapter().bustlyLogout();
  if (!result.success) {
    throw new Error(result.error || "Failed to sign out from Bustly.");
  }
}

export async function openBustlyWorkspaceIntegrations(
  workspaceId: string,
  options?: BustlyWorkspaceIntegrationsOptions,
): Promise<void> {
  const normalizedWorkspaceId = workspaceId.trim();
  const host = getRendererHostAdapter();
  if (host.bustlyOpenWorkspaceIntegrations) {
    const result = await host.bustlyOpenWorkspaceIntegrations(
      normalizedWorkspaceId,
      options,
    );
    if (!result.success) {
      throw new Error(result.error || "Failed to open workspace integrations.");
    }
    return;
  }
  if (normalizedWorkspaceId && host.bustlyOpenWorkspaceSettings) {
    const result = await host.bustlyOpenWorkspaceSettings(
      normalizedWorkspaceId,
    );
    if (!result.success) {
      throw new Error(result.error || "Failed to open workspace settings.");
    }
    return;
  }
  if (host.bustlyOpenSettings) {
    const result = await host.bustlyOpenSettings();
    if (!result.success) {
      throw new Error(result.error || "Failed to open settings.");
    }
  }
}

export async function reportBustlyIssue(): Promise<{
  archivePath: string;
  stateDir: string;
  outputDir: string;
}> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.runtime.report-issue",
    scope: "bustly-runtime",
  });
}

export async function resolveBustlyPathAccess(
  path: string,
): Promise<BustlyResolvedPathAccess> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.path.resolve",
    payload: { path },
    scope: "bustly-path",
  });
}

export async function importBustlyPathFile(params: {
  name?: string;
  relativePath?: string;
  contentBase64: string;
}): Promise<BustlyImportedPathResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.path.import",
    payload: params,
    scope: "bustly-path",
  });
}

export async function getBustlyCronStatus(): Promise<BustlyCronStatusSummary> {
  return await requestBustlyRuntimeMethod({
    method: "cron.status",
    payload: {},
    scope: "bustly-cron-status",
  });
}

export async function listBustlyCronJobsPage(params?: {
  includeDisabled?: boolean;
  limit?: number;
  offset?: number;
  query?: string;
  enabled?: "all" | "enabled" | "disabled";
  sortBy?: "nextRunAtMs" | "updatedAtMs" | "name";
  sortDir?: "asc" | "desc";
}): Promise<BustlyCronListPageResult> {
  return await requestBustlyRuntimeMethod({
    method: "cron.list",
    payload: params ?? {},
    scope: "bustly-cron-list",
  });
}

export async function listBustlyCronJobs(params?: {
  includeDisabled?: boolean;
  query?: string;
  enabled?: "all" | "enabled" | "disabled";
  sortBy?: "nextRunAtMs" | "updatedAtMs" | "name";
  sortDir?: "asc" | "desc";
}): Promise<BustlyCronJob[]> {
  const jobs: BustlyCronJob[] = [];
  let offset = 0;

  while (true) {
    const page = await listBustlyCronJobsPage({
      includeDisabled: params?.includeDisabled,
      query: params?.query,
      enabled: params?.enabled,
      sortBy: params?.sortBy,
      sortDir: params?.sortDir,
      limit: 200,
      offset,
    });
    jobs.push(...page.jobs);
    if (!page.hasMore || page.nextOffset === null) {
      return jobs;
    }
    offset = page.nextOffset;
  }
}

export async function addBustlyCronJob(
  job: BustlyCronJobCreate,
): Promise<BustlyCronJob> {
  return await requestBustlyRuntimeMethod({
    method: "cron.add",
    payload: job,
    scope: "bustly-cron-add",
  });
}

export async function updateBustlyCronJob(params: {
  id: string;
  patch: BustlyCronJobPatch;
}): Promise<BustlyCronJob> {
  return await requestBustlyRuntimeMethod({
    method: "cron.update",
    payload: params,
    scope: `bustly-cron-update-${params.id}`,
  });
}

export async function removeBustlyCronJob(
  id: string,
): Promise<{ ok: true; removed: boolean }> {
  return await requestBustlyRuntimeMethod({
    method: "cron.remove",
    payload: { id },
    scope: `bustly-cron-remove-${id}`,
  });
}

export async function runBustlyCronJob(params: {
  id: string;
  mode?: "due" | "force";
}): Promise<{ ok: boolean; ran?: boolean; reason?: string }> {
  return await requestBustlyRuntimeMethod({
    method: "cron.run",
    payload: params,
    scope: `bustly-cron-run-${params.id}`,
  });
}

export function subscribeBustlyCronEvents(
  listener: (payload: BustlyCronEvent) => void,
): () => void {
  return subscribeBustlyGatewayEvent<BustlyCronEvent>("cron", listener);
}

export function subscribeBustlyHeartbeatEvents(
  listener: (payload: BustlyHeartbeatGatewayPayload) => void,
): () => void {
  return subscribeBustlyGatewayEvent<BustlyHeartbeatGatewayPayload>(
    "heartbeat",
    listener,
  );
}

export function subscribeBustlyChatEvents(
  listener: (payload: BustlyChatEvent) => void,
): () => void {
  return subscribeBustlyGatewayEvent<BustlyChatEvent>("chat", listener);
}

export async function listSelfEvolutionSuggestions(params?: {
  agentId?: string;
  agentDir?: string;
  sessionKey?: string;
  runId?: string;
  status?: SelfEvolutionSuggestionStatus | SelfEvolutionSuggestionStatus[];
  limit?: number;
}): Promise<{ suggestions: SelfEvolutionSuggestion[] }> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.suggestions.list",
    payload: params ?? {},
    scope: "self-evolution-suggestions-list",
  });
}

export async function triggerSelfEvolutionReview(params: {
  sessionKey: string;
  runId: string;
  language?: string;
}): Promise<{ accepted: true; sessionKey: string; runId: string }> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.review.trigger",
    payload: params,
    scope: `self-evolution-review-trigger-${params.runId}`,
  });
}

export async function getSelfEvolutionSuggestion(params: {
  agentId?: string;
  agentDir?: string;
  sessionKey?: string;
  suggestionId: string;
}): Promise<{ suggestion: SelfEvolutionSuggestion }> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.suggestions.get",
    payload: params,
    scope: `self-evolution-suggestion-${params.suggestionId}`,
  });
}

export async function resolveSelfEvolutionSuggestion(params: {
  agentId?: string;
  agentDir?: string;
  sessionKey?: string;
  suggestionId: string;
  action: "approved" | "dismissed";
}): Promise<{
  suggestion: SelfEvolutionSuggestion;
  applyRunId?: string;
  enqueued: boolean;
}> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.suggestions.resolve",
    payload: params,
    scope: `self-evolution-suggestion-resolve-${params.suggestionId}`,
  });
}

export async function confirmSelfEvolutionMemory(params: {
  agentId?: string;
  agentDir?: string;
  sessionKey?: string;
  suggestionId: string;
  memoryType: "short_term" | "long_term";
  heading: string;
  body: string;
}): Promise<{
  suggestion: SelfEvolutionSuggestion;
  applyRunId?: string;
  enqueued: boolean;
}> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.memory.confirm",
    payload: params,
    scope: `self-evolution-memory-confirm-${params.suggestionId}`,
  });
}

export async function confirmSelfEvolutionWorkflowSkill(params: {
  agentId?: string;
  agentDir?: string;
  sessionKey?: string;
  suggestionId: string;
  skillKey: string;
  displayName?: string;
  description: string;
  body: string;
}): Promise<{
  suggestion: SelfEvolutionSuggestion;
}> {
  return await requestBustlyRuntimeMethod({
    method: "self_evolution.workflow_skill.confirm",
    payload: params,
    scope: `self-evolution-workflow-skill-confirm-${params.suggestionId}`,
  });
}

export function subscribeSelfEvolutionSuggestionUpdated(
  listener: (payload: SelfEvolutionSuggestionUpdatedPayload) => void,
): () => void {
  return subscribeBustlyGatewayEvent<SelfEvolutionSuggestionUpdatedPayload>(
    "self_evolution.suggestion.updated",
    listener,
  );
}

export function subscribeSelfEvolutionReviewStatus(
  listener: (payload: SelfEvolutionReviewStatusPayload) => void,
): () => void {
  return subscribeBustlyGatewayEvent<SelfEvolutionReviewStatusPayload>(
    "self_evolution.review.status",
    listener,
  );
}

export async function listBustlyHeartbeats(
  workspaceId?: string,
): Promise<BustlyHeartbeatListResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.list",
    payload: workspaceId ? { workspaceId } : {},
    scope: "bustly-heartbeats",
  });
}

export async function getBustlyHeartbeat(params: {
  workspaceId?: string;
  agentId: string;
}): Promise<BustlyHeartbeatCard> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.get",
    payload: params,
    scope: `bustly-heartbeat-get-${params.agentId}`,
  });
}

export async function listBustlyHeartbeatEvents(params?: {
  workspaceId?: string;
  agentId?: string;
}): Promise<BustlyHeartbeatEventsListResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.events.list",
    payload: params ?? {},
    scope: "bustly-heartbeat-events",
  });
}

export async function updateBustlyHeartbeatEventStatus(params: {
  workspaceId?: string;
  agentId: string;
  eventId: string;
  status: BustlyHeartbeatEvent["status"];
}): Promise<BustlyHeartbeatEventStatusUpdateResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.events.update-status",
    payload: params,
    scope: `bustly-heartbeat-event-status-${params.agentId}-${params.eventId}`,
  });
}

export async function saveBustlyHeartbeat(params: {
  workspaceId?: string;
  agentId: string;
  content: string;
}): Promise<BustlyHeartbeatCard> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.save",
    payload: params,
    scope: `bustly-heartbeat-save-${params.agentId}`,
  });
}

export async function toggleBustlyWorkspaceHeartbeats(params: {
  workspaceId?: string;
  enabled: boolean;
}): Promise<BustlyHeartbeatListResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.toggle-workspace",
    payload: params,
    scope: `bustly-heartbeat-toggle-workspace-${params.workspaceId ?? "active"}`,
  });
}

export async function deleteBustlyHeartbeat(params: {
  workspaceId?: string;
  agentId: string;
}): Promise<{ ok: boolean; workspaceId: string; agentId: string }> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.delete",
    payload: params,
    scope: `bustly-heartbeat-delete-${params.agentId}`,
  });
}

export async function scanBustlyHeartbeats(
  workspaceId?: string,
  options?: { force?: boolean; source?: string },
): Promise<
  BustlyHeartbeatListResult & {
    runs: Array<{
      agentId: string;
      result:
        | { status: "ran"; durationMs: number }
        | { status: "skipped"; reason: string }
        | { status: "failed"; reason: string };
    }>;
  }
> {
  const payload: Record<string, unknown> = {};
  if (workspaceId) {
    payload.workspaceId = workspaceId;
  }
  if (options?.force === true) {
    payload.force = true;
  }
  if (typeof options?.source === "string" && options.source.trim()) {
    payload.source = options.source.trim();
  }
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.scan",
    payload,
    scope: "bustly-heartbeat-scan",
  });
}

export async function listBustlyRunningHeartbeats(
  workspaceId?: string,
): Promise<BustlyHeartbeatRunningResult> {
  return await requestBustlyRuntimeMethod({
    method: "bustly.heartbeats.running",
    payload: workspaceId ? { workspaceId } : {},
    scope: "bustly-heartbeat-running",
  });
}

export async function uploadAgentRunLedgerCloudEvents(params?: {
  workspaceId?: string;
  limit?: number;
}): Promise<AgentRunLedgerCloudUploadResult> {
  return await requestBustlyRuntimeMethod({
    method: "agent.run.cloud-upload",
    payload: params ?? {},
    scope: "agent-run-ledger-cloud-upload",
  });
}

export async function getAgentRunTimeline(
  params: AgentRunTimelineParams,
): Promise<AgentRunTimelineResult> {
  return await requestBustlyRuntimeMethod({
    method: "agent.run.timeline",
    payload: params,
    scope: `agent-run-timeline-${params.runId}`,
  });
}

export async function listAgentRunLedgerCloudEvents(params?: {
  workspaceId?: string;
  limit?: number;
}): Promise<AgentRunLedgerCloudEventsListResult> {
  return await requestBustlyRuntimeMethod({
    method: "agent.run.cloud-events.list",
    payload: params ?? {},
    scope: "agent-run-ledger-cloud-events",
  });
}

export async function getAgentRunLedgerCloudEventContent(params: {
  workspaceId?: string;
  eventId: string;
}): Promise<AgentRunLedgerCloudContentResult> {
  return await requestBustlyRuntimeMethod({
    method: "agent.run.cloud-content.get",
    payload: params,
    scope: `agent-run-ledger-cloud-content-${params.eventId}`,
  });
}
